import _ from "lodash";
import {
  isHideTradingSection,
} from "../../../../utils";
import {TRADING_OBJECT_TYPE_TRANSACTIONS, TRANSACTION_TYPE, TRANSACTION_TYPE_VALUES} from "../../../../constants";
import {getPortfolioInstruments} from "../tradingOperations";

/**
 * Returns the first tab key that has transactions, or the first tab key as fallback.
 */
export const getDefaultActiveTab = (portfolio, tabs) => {
  if (!tabs || !tabs.length) return null;
  const firstPopulated = tabs.find(tab => !_.isEmpty(_.get(portfolio, ['transactions', tab.key])));
  return firstPopulated ? firstPopulated.key : tabs[0].key;
};

const DEFAULT_TRADE_OPTIONS = [
  TRANSACTION_TYPE_VALUES.BUY,
  TRANSACTION_TYPE_VALUES.SELL,
  TRANSACTION_TYPE_VALUES.SWITCH,
];

/**
 * Returns portfolio identifier. For virtual portfolios `portfolioId` is missing, use `id` instead.
 */
export const getPortfolioId = (portfolio, isVirtual) => {
  return isVirtual ? portfolio.id : portfolio.portfolioId;
};

/**
 * Derives the available tabs for a portfolio based on its configuration and allowed trade options.
 */
export const getPortfolioTabs = (portfolio, isVirtual, allowedTradeOptions) => {
  const tabs = [];

  const isPassiveDepot = _.get(portfolio, 'data.is_passive');
  const isModelportfolio = _.get(portfolio, 'isModelportfolio');
  const isETF = _.get(portfolio, 'isETF');

  let tradeOptions;
  if (isPassiveDepot) {
    tradeOptions = [TRANSACTION_TYPE_VALUES.SELL];
  } else {
    tradeOptions = allowedTradeOptions || DEFAULT_TRADE_OPTIONS;
  }

  if (isModelportfolio || isETF) {
    tradeOptions = tradeOptions.filter(key => key !== TRANSACTION_TYPE_VALUES.SWITCH);
  }

  if (_.get(portfolio, 'data.trading_enabled')) {
    tradeOptions.forEach(key => {
      const availableInstruments = getPortfolioInstruments(portfolio, portfolio.transactions, isVirtual, key);
      const transactions = _.get(portfolio, ['transactions', key], []);

      // For model portfolios, hide tab if no instruments available and no existing transactions
      if (isModelportfolio && _.isEmpty(availableInstruments) && _.isEmpty(transactions)) {
        return;
      }

      if (TRANSACTION_TYPE[key]
        && !isHideTradingSection(key, availableInstruments, transactions)
      ) {
        tabs.push({key, label: TRANSACTION_TYPE[key]});
      }
    });
  }

  if (_.get(portfolio, 'data.saving_plan_trading_enabled')) {
    tabs.push({key: TRANSACTION_TYPE_VALUES.SAVINGS_PLAN, label: TRANSACTION_TYPE.savings_plan});
  }
  if (!isVirtual && _.get(portfolio, 'data.payout_plan_trading_enabled')) {
    tabs.push({key: TRANSACTION_TYPE_VALUES.PAYOUT_PLAN, label: TRANSACTION_TYPE.payout_plan});
  }
  if (!isVirtual && _.get(portfolio, 'data.switch_plan_trading_enabled')) {
    tabs.push({key: TRANSACTION_TYPE_VALUES.SWITCH_PLAN, label: TRANSACTION_TYPE.switch_plan});
  }

  return tabs;
};

const PROCESS_ERROR_KEYS = Object.keys(TRADING_OBJECT_TYPE_TRANSACTIONS).map(process => `${process}_errors`);

/**
 * Checks whether a portfolio has any validation errors across all error groups.
 */
export const portfolioHasErrors = (portfolio) => {
  return _.some(PROCESS_ERROR_KEYS, (errorPath) => {
    const errorsObj = portfolio[errorPath];

    return errorsObj && _.some(Object.values(errorsObj), Boolean)
  });
};

const PROCESS_PTF_ERROR_KEYS = Object.keys(TRADING_OBJECT_TYPE_TRANSACTIONS).map(process => `${process}_errors.portfolio`);
export const portfolioFirstError = (portfolio) => {
  const errorPath = _.find(PROCESS_PTF_ERROR_KEYS, (errorPath) => !!_.get(portfolio, errorPath));

  return errorPath ? _.get(portfolio, errorPath) : undefined;
};

/**
 * Returns a copy of the specified error group with the given key removed.
 * Returns null if no errors remain.
 *
 * @param {Object} portfolio - Portfolio object
 * @param {string} errorGroup - Error group key (e.g. 'trading_errors', 'savings_plan_errors')
 * @param {string} errorKey - Specific error key to remove
 */
export const getErrorsWithout = (portfolio, errorGroup, errorKey) => {
  const errors = portfolio[errorGroup];
  if (!errors) return null;
  const updated = _.omit(errors, errorKey);
  return _.some(Object.values(updated), Boolean) ? updated : null;
};

/**
 * Returns a map of tab key -> boolean indicating which tabs have errors.
 */
export const getTabErrors = (portfolio) => {
  if (!portfolio) return {};

  const errors = {};

  const tradingErrors = portfolio.trading_errors;
  if (tradingErrors) {
    [TRANSACTION_TYPE_VALUES.BUY, TRANSACTION_TYPE_VALUES.SELL, TRANSACTION_TYPE_VALUES.SWITCH].forEach(key => {
      if (tradingErrors[key]) {
        errors[key] = true;
      }
    });
  }

  if (portfolio.savings_plan_errors) {
    errors[TRANSACTION_TYPE_VALUES.SAVINGS_PLAN] = true;
  }
  if (portfolio.payout_plan_errors) {
    errors[TRANSACTION_TYPE_VALUES.PAYOUT_PLAN] = true;
  }
  if (portfolio.switch_plan_errors) {
    errors[TRANSACTION_TYPE_VALUES.SWITCH_PLAN] = true;
  }

  return errors;
};
